import { css } from "@linaria/core";
import React, { memo, useCallback, useState } from "react";
import Modal from "@ui/Modal";
import { colors, shadows } from "@ui/Theme";
import { H4, Text } from "@ui/Text";
import { useDashboardEditorContext } from "../../../../../hook/context/editor";
import type { DashboardBlockModelT } from "../../../../../schema/dashboard";
import { useDashboardEditorState } from "../../../../../hook/store/editor";
import { useDashboardUnsaved } from "../../../../../hook/useDashboardUnsaved";
import t from "../../../../../../../../i18n";
import type { BlockPlotModelT } from "../../../../../schema/block/plot";
import Tab from "@ui/NavV2/Tab";
import classNames from "classnames";
import NavV2 from "@ui/NavV2";
import { useSettings } from "../../../../../../../../api/useSettings";
import { customTemplates, presetTemplates, type BlockTemplate } from "./templates";
import type { BlockModelT } from "../../../../../schema/block";

export const AddBlockModal = memo(
  ({
    dashboardId,
    blockId,
    blockPosition,
    opened,
    onRequestClose,
    onBlockAdded,
  }: {
    dashboardId: string;
    blockId: string;
    blockPosition: [number, number, number, number] | null;
    opened: boolean;
    onRequestClose: () => void;
    onBlockAdded?: (block: BlockModelT) => void;
  }) => {
    const editor = useDashboardEditorContext();
    const editorState = useDashboardEditorState(editor?.store);
    const [_, setUnsaved] = useDashboardUnsaved({ scope: dashboardId });
    const [templateCategory, setTemplateCategory] = useState<"custom" | "preset">("custom");

    const addBlockCallback = useCallback(
      async (block: BlockTemplate) => {
        if (!editorState?.blocks || !blockPosition) return;

        const newBlock: DashboardBlockModelT = {
          ...block,
          id: blockId,
          timestamp: Date.now(),
          dashboardId,
          position: blockPosition,
        };

        // Sync new block if some are already sync
        const firstSyncedBlockPlot =
          editorState.blocks.filter(
            (block): block is BlockPlotModelT => block.kind === "PLOT",
          )[0] ?? null;

        if (firstSyncedBlockPlot && newBlock.kind === "PLOT") {
          newBlock.config.options = {
            ...newBlock.config.options,
            temporalZoom: firstSyncedBlockPlot.config?.options?.temporalZoom,
          };
        }

        void editor?.updateDashboard({
          ...editorState.dashboard,
          blocks: [...editorState.blocks.map(({ id }) => id), newBlock.id],
        });
        await editor?.updateBlock(newBlock);
        setUnsaved(true);
        onRequestClose();
        onBlockAdded?.(newBlock);
        editor?.focus?.(newBlock);
      },
      [
        editorState?.blocks,
        editorState?.dashboard,
        blockPosition,
        blockId,
        dashboardId,
        editor,
        onBlockAdded,
        setUnsaved,
        onRequestClose,
      ],
    );

    return (
      <Modal
        isOpen={opened}
        title={t("project.dashboard.actions.block.add")}
        onRequestClose={onRequestClose}
        size="xl"
      >
        <NavV2 className={navBorder}>
          <Tab
            onClick={() => setTemplateCategory("custom")}
            className={classNames({ active: templateCategory === "custom" })}
            data-cy="custom-tab"
          >
            {t("project.dashboard.block.modal.addCustom")}
          </Tab>
          <Tab
            onClick={() => setTemplateCategory("preset")}
            className={classNames({ active: templateCategory === "preset" })}
            data-cy="preset-tab"
          >
            {t("project.dashboard.block.modal.addPreset")}
          </Tab>
        </NavV2>

        <div className={blockTemplateContent}>
          <TemplateList templateCategory={templateCategory} onAddBlock={addBlockCallback} />
        </div>
      </Modal>
    );
  },
);

const TemplateList = memo(
  ({
    templateCategory,
    onAddBlock,
  }: {
    templateCategory: "custom" | "preset";
    onAddBlock: (block: BlockTemplate) => void;
  }) => {
    const settings = useSettings();
    const templates = templateCategory === "custom" ? customTemplates : presetTemplates;

    const filteredTemplates = templates.map(({ title, options }) => ({
      title,
      options:
        templateCategory === "custom"
          ? options
          : options.filter(({ block }) => {
              const allowFinancialImpact =
                settings.DISPLAY_FINANCIAL_IMPACT_DASHBOARD &&
                block.kind === "PRESET" &&
                block.data.preset === "FINANCIAL_IMPACT";

              const allowFaultEvents =
                settings.FAULT_EVENTS_ENABLED &&
                block.kind === "PRESET" &&
                block.data.preset === "PRIORITY_FAULT_EVENTS";

              const allowAverageLosses =
                block.kind === "PRESET" && block.data.preset === "AVERAGE_LOSSES";

              const allowProductionMonitoring =
                block.kind === "PRESET" && block.data.preset === "PRODUCTION_MONITORING";

              return (
                allowFinancialImpact ||
                allowFaultEvents ||
                allowAverageLosses ||
                allowProductionMonitoring
              );
            }),
    }));

    return (
      <>
        {filteredTemplates.map(({ title, options }) => (
          <section key={title} className={blockTemplateSection}>
            {templateCategory === "custom" && <H4>{title}</H4>}
            <div className={blockSelectorGrid}>
              {options.map(({ icon, title, description, block }) => (
                <button key={title} className={blockSelector} onClick={() => onAddBlock(block)}>
                  {icon()}
                  <Text color={colors.carbon800} className={templateTitle}>
                    {title}
                  </Text>
                  <Text type="body2" as="span">
                    {description}
                  </Text>
                </button>
              ))}
            </div>
          </section>
        ))}
      </>
    );
  },
);

const blockTemplateContent = css`
  padding: 24px 0;
  overflow-y: auto;
  color: ${colors.carbon800};
`;

const blockTemplateSection = css`
  display: contents;

  & + & {
    margin-top: 16px;
  }

  h4 {
    grid-column: 1 / 3;
    margin-bottom: 8px;
  }
`;

const blockSelectorGrid = css`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 16px;
`;

const blockSelector = css`
  display: grid;
  grid-template-columns: 32px 1fr;
  grid-template-rows: max-content;
  background-color: transparent;
  border: 1px dashed ${colors.titanium200};
  border-radius: 4px;
  padding: 16px;
  text-align: left;
  transition:
    border-color 75ms linear,
    box-shadow 150ms linear;
  cursor: pointer;

  span {
    grid-column: 2 / 3;
  }

  span:last-child {
    color: ${colors.carbon700};
  }

  &:hover {
    border-style: solid;
    border-color: ${colors.lightNitrogen500_Main};
    box-shadow: ${shadows.shadow100};
  }
`;

const templateTitle = css`
  display: flex;
  justify-content: space-between;
  color: ${colors.carbon900};
  font-size: 14px;
`;

const navBorder = css`
  border-bottom: 1px solid;
  border-bottom-color: ${colors.titanium200};
`;
